'use client';
import React, { useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, Shield, Clock, Users } from 'lucide-react';

const rules = [
  { id: 'rule-1', title: 'Eligibility', content: 'Participants must be 18 years or older (or legal age in their jurisdiction). Identity verification (KYC Level 1) is required before joining paid competitions. Users must be in an eligible region — check your account settings for regional availability.' },
  { id: 'rule-2', title: 'Competition Format', content: 'This is a timed multiple-choice trivia tournament. Each round consists of 20 questions across 5 categories: Science, History, Geography, Current Affairs, and General Knowledge. Players have 30 seconds per question. Points are awarded for correct answers plus speed bonuses.' },
  { id: 'rule-3', title: 'Scoring System', content: 'Correct answer: 100 points. Speed bonus: up to 50 additional points based on response time. Wrong answer: 0 points (no penalty). Final ranking is determined by total points. Ties are broken by average response time.' },
  { id: 'rule-4', title: 'Fair Play Policy', content: 'All answers are submitted to Brain Basket servers for validation. Client-side scoring is not accepted. Use of external resources, AI tools, or collaboration during the competition is prohibited and will result in disqualification. Suspicious patterns are reviewed by our Fair Play Engine.' },
  { id: 'rule-5', title: 'Refund Policy', content: 'Entry fees are non-refundable once the competition begins. If a competition is cancelled by Brain Basket, all entry fees are refunded within 48 hours. If a technical issue affects your participation, contact support within 24 hours with evidence for review.' },
];

export default function ActivityRules() {
  const [openId, setOpenId] = useState<string | null>('rule-2');

  return (
    <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 mb-5">
        <BookOpen size={18} style={{ color: '#06B6D4' }} />
        <h2 className="text-base font-bold text-foreground">Rules & Eligibility</h2>
      </div>
      {/* Quick info */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { icon: Clock, label: 'Duration', value: '45 minutes', color: '#A78BFA' },
          { icon: Users, label: 'Format', value: 'Individual', color: '#06B6D4' },
          { icon: Shield, label: 'Age Req.', value: '18+ only', color: '#F59E0B' },
        ]?.map((item) => (
          <div key={`qi-${item?.label}`} className="text-center p-3 rounded-xl"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
            <item.icon size={16} style={{ color: item?.color, margin: '0 auto 6px' }} />
            <p className="text-xs font-bold text-foreground">{item?.value}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)', fontSize: '10px' }}>{item?.label}</p>
          </div>
        ))}
      </div>
      {/* Accordion */}
      <div className="space-y-2">
        {rules?.map((rule) => (
          <div key={rule?.id} className="rounded-xl overflow-hidden transition-all"
            style={{ border: `1px solid ${openId === rule?.id ? 'rgba(124,58,237,0.35)' : 'var(--border)'}` }}>
            <button
              onClick={() => setOpenId(openId === rule?.id ? null : rule?.id)}
              className="w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/30"
            >
              <span className="text-sm font-semibold text-foreground">{rule?.title}</span>
              {openId === rule?.id
                ? <ChevronUp size={14} style={{ color: '#A78BFA', flexShrink: 0 }} />
                : <ChevronDown size={14} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
              }
            </button>
            {openId === rule?.id && (
              <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--border)' }}>
                <p className="text-sm leading-relaxed pt-3" style={{ color: 'var(--muted-foreground)' }}>{rule?.content}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}